import React, { useState, useEffect } from 'react';
import {
  CircularProgress,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TableContainer,
  Paper,
  Typography,
  Chip,
  Tooltip,
  Stack,
  TextField,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CodeIcon from '@mui/icons-material/Code';
import { useNavigate, useParams } from 'react-router-dom';
import Papa from 'papaparse';
import { useGetAdmissionsQuery } from '../redux/medchartApi';

function AdmissionsList() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();
  const { tenantId } = useParams();

  // Debounce search input — 400ms
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const { data, isLoading, error } = useGetAdmissionsQuery({
    search: debouncedSearch || undefined,
    hasPdf: activeFilter === 'pdf_ready' ? true : undefined,
    status: activeFilter === 'pending' ? 'pending' : undefined,
  });

  const admissionsData = data?.results || [];
  const loadingAdmissions = isLoading;

  // Export to CSV function using papaparse
  const exportToCSV = () => {
    const createdAdmissions = admissionsData.filter((admission) => admission.status === 'created');

    const csv = Papa.unparse(createdAdmissions, {
      headers: true,
      columns: [
        'pk',
        'status',
        'first_name',
        'last_name',
        'date_of_birth',
        'gender',
        'ethnicity',
      ],
    });

    // Create a Blob from the CSV data and download it
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.setAttribute('download', 'admissions_created.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <h3>Admissions</h3>

      {/* Search + Quick-filter Row */}
      <Box mb={2} mt={1}>
        <TextField
          size="small"
          label="Search by name or ICD code"
          variant="outlined"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ marginBottom: '12px', minWidth: '300px' }}
        />
        <Stack direction="row" spacing={1} mt={1}>
          <Chip
            label="All"
            clickable
            color={activeFilter === 'all' ? 'primary' : 'default'}
            onClick={() => setActiveFilter('all')}
          />
          <Chip
            label="PDF Ready"
            clickable
            color={activeFilter === 'pdf_ready' ? 'primary' : 'default'}
            onClick={() => setActiveFilter('pdf_ready')}
          />
          <Chip
            label="Pending"
            clickable
            color={activeFilter === 'pending' ? 'primary' : 'default'}
            onClick={() => setActiveFilter('pending')}
          />
        </Stack>
      </Box>

      <Button variant="contained" style={{backgroundColor:"#5ad3d9"}} onClick={exportToCSV}>
        Export Created Admissions to CSV
      </Button>
      {loadingAdmissions ? (
        <Box display="flex" justifyContent="center" mt={2}>
          <CircularProgress />
          <Box ml={2}>Loading admissions...</Box>
        </Box>
      ) : error ? (
        <Box color="error.main" mt={2}>
          Error: {error.message}
        </Box>
      ) : admissionsData.length > 0 ? (
        <TableContainer component={Paper} style={{ marginTop: '20px' }}>
        <Table stickyHeader>
          {/* Table Head */}
          <TableHead>
            <TableRow>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>ID</TableCell>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>First Name</TableCell>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>Last Name</TableCell>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>Date of Birth</TableCell>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>Gender</TableCell>
              <TableCell style={{ backgroundColor: '#5ad3d9', color: '#ffffff', fontWeight: 'bold' }}>Ethnicity</TableCell>
            </TableRow>
          </TableHead>
  
          {/* Table Body */}
          <TableBody>
            {admissionsData.map((admission) => (
              <TableRow
                key={admission.pk}
                hover
                onClick={() => navigate(tenantId ? `/dashboard/${tenantId}/admissions/${admission.pk}` : `/dashboard/admissions/${admission.pk}`)}
                style={{ cursor: 'pointer', 
                  transition: 'background-color 0.3s ease',
                  backgroundColor: admission.status === 'created' ? '#e0f7fa' : 'inherit', // Conditional background color for "created"
                }}
                sx={{
                  '&:hover': { backgroundColor: '#f5f5f5' },
                  '&:nth-of-type(odd)': { backgroundColor: '#fafafa' }, // Striped rows
                }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" style={{ fontWeight: '500' }}>{admission.pk}</Typography>
                    {admission.source === 'plugin' && (
                      <Chip
                        label="Claude Code"
                        size="small"
                        icon={<CodeIcon />}
                        sx={{ ml: 1, fontSize: '0.7rem', bgcolor: 'rgba(56,189,248,0.1)', color: '#38bdf8' }}
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {admission.status === 'created' ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label="Ready"
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ) : (
                    <Tooltip title={`Status: ${admission.status}`}>
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={14} thickness={5} />
                        <Typography variant="body2" color="text.secondary">Generating…</Typography>
                      </Box>
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell>{admission.first_name}</TableCell>
                <TableCell>{admission.last_name}</TableCell>
                <TableCell>{admission.date_of_birth}</TableCell>
                <TableCell>{admission.gender}</TableCell>
                <TableCell>{admission.ethnicity}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      ) : (
        <div>No admissions found.</div>
      )}
    </div>
  );
}

export default AdmissionsList;
